import "./style.scss";

import { KeyboardArrowDown } from "@mui/icons-material";
import { TabContext, TabPanel } from "@mui/lab";
import { Collapse, Tab, Tooltip } from "@mui/material";
import classNames from "classnames";
import type { UserVariables } from "components/shared/AgreementModal";
import SalesTitleSwitcher from "components/shared/ManagementTitleDetails/SalesDetails/SalesTitleSwitcher";
import ScrollShadowWrapper from "components/shared/ScrollShadowWrapper";
import { StyledTabs } from "components/shared/StyledTabs";
import type { IChannel } from "containers/Channels/types";
import type { MissingVariable } from "containers/ContractValidationEdit/utils";
import { BodyHighlight, SmallBody, SmallBodyHighlight } from "helpers/TypographyComponents";
import { isObjectEmpty, nameTransform } from "helpers/utils";
import { resolveAdvanceAmountLetters, useCompanySettings } from "hooks/useCompanySettings";
import type { SetStateAction } from "react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Agreement } from "store/agreementsStore";
import { useAppSelector } from "store/store";
import type { TFetchStatus } from "store/types";
import { EPortalType } from "store/types";

import { ArticleVariablesGroup } from "./ArticleVariablesGroup";
import { getVariableGroupI18nKey, groupAgreementVariables } from "./groupAgreementVariables";
import { useAgreementDraftVariables } from "./useAgreementDraftVariables";
import { useAgreementTitleSource } from "./useAgreementTitleSource";
import { AgreementVariablesList } from "./VariablesList";

interface IPanelVariables {
    agreementStatus: TFetchStatus;
    isDraft: boolean;
    agreement: Agreement | null;
    handleVariableChange: (variable?: UserVariables) => void;
    isOwner: boolean;
    userVariables: UserVariables | undefined;
    setUserVariables: React.Dispatch<SetStateAction<UserVariables | undefined>>;
    hasMultiSignature: boolean;
    missingVariables: MissingVariable[];
    beneficiariesMissingVariables: MissingVariable[];
    channels: IChannel[];
}

const PanelVariables = ({
    agreementStatus,
    isDraft,
    agreement,
    handleVariableChange,
    isOwner,
    userVariables,
    setUserVariables,
    hasMultiSignature,
    missingVariables,
    beneficiariesMissingVariables,
    channels,
}: IPanelVariables) => {
    const { getCompanySettings, portalType } = useCompanySettings();
    const resolvedPortalType = portalType ?? EPortalType.BOOK;
    const { customFields } = useAppSelector((state) => state.customFields);
    const { term } = useAppSelector((state) => state.term);
    const [showVariableList, setShowVariableList] = useState(true);
    const [currentTab, setCurrentTab] = useState("0");
    const [t] = useTranslation();
    const { openingBalanceInLetters, initialAdvanceInLetters } = resolveAdvanceAmountLetters(getCompanySettings);

    const { variablesList, beneficiariesVariablesList, articlesVariablesList } = useAgreementDraftVariables({
        agreement,
        hasMultiSignature,
        showOpeningBalanceInLetters: openingBalanceInLetters,
        showInitialAdvanceInLetters: initialAdvanceInLetters,
        channels,
        setUserVariables,
    });
    const { workTitles, selectedTitleId, showTitleSwitcher, handleSelectTitle } = useAgreementTitleSource({
        portalType: resolvedPortalType,
        agreement,
        termWorkId: term?.workId,
        userVariables,
        customFields,
        usedVariableKeys: variablesList?.map((variable) => variable.name) ?? [],
        setUserVariables,
        handleVariableChange,
    });
    const variableGroups = useMemo(
        () => (variablesList ? groupAgreementVariables(variablesList, resolvedPortalType, customFields) : []),
        [variablesList, resolvedPortalType, customFields],
    );

    const articleIndexes = Object.keys(articlesVariablesList)
        .map(Number)
        .sort((left, right) => left - right);
    const articleVarCount = articleIndexes.reduce(
        (count, index) => count + (articlesVariablesList[index]?.length ?? 0),
        0,
    );
    const beneficiaryVarCount = Object.values(beneficiariesVariablesList).reduce(
        (count, variables) => count + variables.length,
        0,
    );
    const allVarCount = (variablesList?.length ?? 0) + beneficiaryVarCount + articleVarCount;
    const isDocumentVariableFilled = (key: string) => {
        const value = userVariables?.[key];
        return value != null && value !== "";
    };
    const completedVarCount =
        (variablesList?.filter((variable) => isDocumentVariableFilled(variable.name)).length ?? 0) +
        Object.values(beneficiariesVariablesList).reduce(
            (count, variables) =>
                count + variables.filter((variable) => isDocumentVariableFilled(variable.multiBeneficiaryKey)).length,
            0,
        ) +
        articleIndexes.reduce(
            (count, index) =>
                count +
                (articlesVariablesList[index]?.filter((variable) =>
                    isDocumentVariableFilled(variable.multiBeneficiaryKey),
                ).length ?? 0),
            0,
        );
    const missingVariablesCount = allVarCount - completedVarCount;
    const hasCompletedVar = !missingVariablesCount;
    const hasRepeatableContentBlock = !!document.querySelector(".Repeatable__content");
    const beneficiaries = agreement?.userVariables?.beneficiaries;
    const varTooltip = {
        incomplete: t("ContractValidationEdit.VariablesCountTooltip", { completedVarCount, allVarCount }),
        completed: t("ContractValidationEdit.AllVariablesComplete"),
    };

    if (agreementStatus !== "succeeded") return null;
    if (!variablesList?.length && isObjectEmpty(beneficiariesVariablesList) && articleIndexes.length === 0) return null;

    return (
        <>
            <div className="contract-validation-edit-right-panel-variables">
                <div
                    className="contract-validation-edit-right-panel-variables-title-wrapper"
                    onClick={() => setShowVariableList(!showVariableList)}
                >
                    <div className="contract-validation-edit-right-panel-variables-title">
                        <BodyHighlight color="black">{t("ContractValidationEdit.ContractVariables")}</BodyHighlight>
                        {isDraft && (
                            <Tooltip title={hasCompletedVar ? varTooltip.completed : varTooltip.incomplete}>
                                <div>
                                    <SmallBody
                                        color={hasCompletedVar ? "success" : "danger"}
                                        style={{ fontWeight: "300" }}
                                    >{`${completedVarCount}/${allVarCount} ${t(
                                        "ContractValidationEdit.Completed",
                                    )}`}</SmallBody>
                                </div>
                            </Tooltip>
                        )}
                    </div>
                    <KeyboardArrowDown />
                </div>
                <Collapse in={showVariableList} timeout={500}>
                    {hasMultiSignature && hasRepeatableContentBlock && (
                        <TabContext value={currentTab}>
                            <StyledTabs
                                variant="scrollable"
                                scrollButtons
                                value={currentTab}
                                className="beneficiaries-variables-tabs"
                                onChange={(_e, tabValue) => setCurrentTab(tabValue)}
                            >
                                {beneficiaries?.map((beneficiary, index) => {
                                    const hasIncompleteVar = beneficiariesMissingVariables.some(
                                        (variable) => variable.beneficiaryName === (beneficiary.name ?? ""),
                                    );
                                    return (
                                        <Tab
                                            label={
                                                <div className="beneficiary-tab-label">
                                                    <SmallBodyHighlight>
                                                        {nameTransform(beneficiary.name ?? "")}
                                                    </SmallBodyHighlight>
                                                    {hasIncompleteVar && <div className="variable-dot-danger" />}
                                                </div>
                                            }
                                            value={String(index)}
                                            key={`variables-tab-${beneficiary.name ?? ""}`}
                                            sx={{ textTransform: "none" }}
                                        />
                                    );
                                })}
                            </StyledTabs>
                            {Object.keys(beneficiariesVariablesList).map((beneficiaryId) => (
                                <TabPanel
                                    value={String(beneficiaryId)}
                                    key={`beneficiary-tab-panel-${beneficiaryId}`}
                                    className="beneficiary-variables-tab-panel"
                                >
                                    <AgreementVariablesList
                                        variables={beneficiariesVariablesList[beneficiaryId]}
                                        onVariableBlur={handleVariableChange}
                                        isOwner={isOwner}
                                        userVariables={userVariables}
                                        setUserVariables={setUserVariables}
                                        hasMultiBeneficiaries
                                        missingVariables={missingVariables}
                                        beneficiariesMissingVariables={beneficiariesMissingVariables}
                                    />
                                </TabPanel>
                            ))}
                        </TabContext>
                    )}
                    {hasMultiSignature && !isObjectEmpty(beneficiariesVariablesList) && (
                        <SmallBodyHighlight color="black">
                            {t("ContractValidationEdit.GlobalVariables")}
                        </SmallBodyHighlight>
                    )}
                    <div className="beneficiary-variables-tab-panel">
                        {agreement?.userVariables && variablesList && (
                            <ScrollShadowWrapper className="agreement-step-2-variables-wrapper">
                                <div className="agreement-variable-groups">
                                    <ArticleVariablesGroup
                                        articlesVariablesList={articlesVariablesList}
                                        workTitles={workTitles}
                                        portalType={resolvedPortalType}
                                        onVariableBlur={handleVariableChange}
                                        isOwner={isOwner}
                                        userVariables={userVariables}
                                        setUserVariables={setUserVariables}
                                        missingVariables={missingVariables}
                                        beneficiariesMissingVariables={beneficiariesMissingVariables}
                                    />
                                    {showTitleSwitcher && articleIndexes.length === 0 && (
                                        <SalesTitleSwitcher
                                            titles={workTitles}
                                            selectedTitleId={selectedTitleId ?? workTitles[0].id}
                                            onSelect={handleSelectTitle}
                                            showAll={false}
                                            label={t("ContractValidationEdit.TitleSourcePickerLabel")}
                                        />
                                    )}
                                    {variableGroups.map((group) => (
                                        <div className="agreement-variable-group" key={group.id}>
                                            <SmallBodyHighlight color="black">
                                                {t(getVariableGroupI18nKey(group.id, resolvedPortalType))}
                                            </SmallBodyHighlight>
                                            <AgreementVariablesList
                                                variables={group.variables}
                                                onVariableBlur={handleVariableChange}
                                                isOwner={isOwner}
                                                userVariables={userVariables}
                                                setUserVariables={setUserVariables}
                                                missingVariables={missingVariables}
                                                beneficiariesMissingVariables={beneficiariesMissingVariables}
                                                embedded
                                            />
                                        </div>
                                    ))}
                                </div>
                            </ScrollShadowWrapper>
                        )}
                    </div>
                </Collapse>
            </div>
            <div
                className={classNames({
                    "contract-validation-edit-right-panel-dividers": true,
                    "variables-panel": showVariableList,
                })}
            ></div>
        </>
    );
};

export default PanelVariables;
